import { Circ, Elastic, Expo, TimelineLite, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {array} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import { Button, OutlinedButton } from "../gameobjects/Button";

import { Application } from "../Application";
import * as VO from "../data/VO";
import { CaptionManager } from "../managers/CaptionManager";
import { LoadScreen } from "../states/LoadScreen";
import { MainMenu } from "../states/MainMenu";

import { random } from "html-living-framework/lib/Utils/ArrayUtils";
import { sendToFront } from "html-living-framework/lib/Utils/PixiUtils";
import { Emitter } from "pixi-particles";
import { texture } from "../core/AssetHelpers";
import {BadgePopup} from "../layout/BadgePopup";

export abstract class AbstractBadgePopup extends HLF.Popup {

    public static stickers = {
        trash: [0, 1, 2],
        lightsout: [0, 1, 2],
        planterparty: [0, 1, 2],
    };
    public static latestSticker = {
        trash: undefined as number | undefined,
        lightsout: undefined as number | undefined,
        planterparty: undefined as number | undefined,
    };
    public blackout = new PIXI.Graphics();
    public readonly bg = this.addChild(new Sprite());
    public readonly particlesContainer = this.addChild(new PIXI.Container());
    public readonly homeButton = this.addChild(new OutlinedButton());
    public readonly badge = this.addChild(new Sprite());
    public readonly grover = this.addChild(new Sprite());
    public readonly elmo = this.addChild(new Sprite());
    public readonly oscar = this.addChild(new Sprite());
    public readonly abby = this.addChild(new Sprite());

    public readonly trashSticker = [
        this.addChild(new Sprite()),
        this.addChild(new Sprite()),
        this.addChild(new Sprite()),
    ];
    public readonly lightsoutSticker = [
        this.addChild(new Sprite()),
        this.addChild(new Sprite()),
        this.addChild(new Sprite()),
    ];
    public readonly planterpartySticker = [
        this.addChild(new Sprite()),
        this.addChild(new Sprite()),
        this.addChild(new Sprite()),
    ];

    protected abstract badgeVO: string[];
    protected idleTimeline?: TimelineLite;
    protected starParticles = new Emitter(this.particlesContainer, [texture("star")], {
        alpha: {
            start: 1,
            end: 0,
        },
        scale: {
            start: 1,
            end: 0.25,
            minimumScaleMultiplier: 0.25,
        },
        color: {
            start: "#ffffff",
            end: "#ffffff",
        },
        speed: {
            start: 250,
            end: 50,
            minimumSpeedMultiplier: 0.5,
        },
        acceleration: {
            x: 0,
            y: 0,
        },
        maxSpeed: 0,
        startRotation: {
            min: 0,
            max: 0,
        },
        noRotation: false,
        rotationSpeed: {
            min: 0,
            max: 0,
        },
        lifetime: {
            min: 3,
            max: 4,
        },
        blendMode: "normal",
        frequency: 0.1,
        emitterLifetime: 0.2,
        maxParticles: 1500,
        pos: {
            x: 0,
            y: 0,
        },
        addAtBack: false,
        spawnType: "ring",
        particlesPerWave: 75,
        spawnCircle: {
            x: 0,
            y: 0,
            r: 400,
            minR: 300,
        },
    });

    constructor() {
        super();
        BadgePopup.call(this);
        this.homeButton.onClick.add(() => this.onHomeButtonClicked());
        this.homeButton.clickSfx = "buttonclick";
        this.elmo.visible = false;
        this.oscar.visible = false;
        this.abby.visible = false;
        this.grover.visible = true;
        this.trashSticker.forEach((s) => s.visible = false);
        this.lightsoutSticker.forEach((s) => s.visible = false);
        this.planterpartySticker.forEach((s) => s.visible = false);
        this.starParticles.emit = false;
        this.particlesContainer.position.set(512, 384);
    }

    public init(pSize: PIXI.Point) {
        super.init(pSize);
        this.blackout.clear();
        this.blackout.beginFill(0x000000, 0.5);
        this.blackout.drawRect(0, 0, pSize.x, pSize.y);
        this.blackout.endFill();
        this.blackout.on("tap", this.OnBlackoutClicked.bind(this)); // TODO: this should be in the framework

        this.idleTimeline = new TimelineLite();
        this.idleTimeline.call(() => {
            console.log("idle 30 seconds, going to home screen");
            this.onHomeButtonClicked({auto: true});
        }).delay(30).pause();

        this.trashSticker.forEach((s, i) => s.visible = !(AbstractBadgePopup.stickers.trash.includes(i)));
        this.lightsoutSticker.forEach((s, i) => s.visible = !(AbstractBadgePopup.stickers.lightsout.includes(i)));
        this.planterpartySticker.forEach((s, i) => s.visible = !(AbstractBadgePopup.stickers.planterparty.includes(i)));

        if (AbstractBadgePopup.latestSticker.lightsout) {
            sendToFront(this.lightsoutSticker[AbstractBadgePopup.latestSticker.lightsout]);
        }
        if (AbstractBadgePopup.latestSticker.trash) {
            sendToFront(this.lightsoutSticker[AbstractBadgePopup.latestSticker.trash]);
        }
        if (AbstractBadgePopup.latestSticker.planterparty) {
            sendToFront(this.lightsoutSticker[AbstractBadgePopup.latestSticker.planterparty]);
        }
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);
        this.position.set((-pSize.x / 2) + (pSize.x - 1024) / 2, -pSize.y / 2);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.starParticles.update(pDeltaTime);
    }

    public hide() {
        if (this.idleTimeline !== undefined) {
            this.idleTimeline?.kill();
            this.idleTimeline?.clear();
        }
        this.starParticles.cleanup();
        super.hide();
    }

    public show(pToken: HLF.IPopupToken): void {
        super.show(pToken);
        this._keyboardToClose = false; // workaround for bug in HLF
    }

    protected AnimateIn(pCallback: () => void): void {
        new TimelineLite()
            .from(this.blackout, 1, {pixi: {alpha: 0}})
            .from(this.bg, 1, {pixi: {scaleX: 0}, ease: Elastic.easeOut}, 0)
            .staggerFrom([this.elmo, this.grover, this.abby], 1, {pixi: {y: 768}, ease: Expo.easeInOut}, 0.25, 0);
        pCallback();
        this.starParticles.playOnce();
        HLF.Application.instance.audio.play("badge-fanfare", 1, false, HLF.AudioCategory.SFX);
        Application.instance.voiceover.playVO([random([VO.BADGE01, VO.BADGEB01]), ...this.badgeVO, VO.BADGE05], () => {
            this.idleTimeline?.restart(true);
        });

        HLF.Application.instance.audio.play("badge-reward-sticker", 1, false, HLF.AudioCategory.SFX);
    }

    protected AnimateOut(pCallback: () => void): void {
        this.starParticles.emit = false;
        pCallback();
    }

    protected onHomeButtonClicked(pParams?: {auto: boolean}) {
        if (pParams?.auto) {
            PubSub.publishSync("home button auto clicked");
        }
        else {
            PubSub.publishSync("home button clicked");
        }
        this.hide();
        const steps = [
            HLF.TransitionStep.ShowLoadScreen,
            // HLF.TransitionStep.AnimCurrentOut,
            HLF.TransitionStep.RemoveCurrent,
            HLF.TransitionStep.UnloadAssets,
            HLF.TransitionStep.LoadAssets,
            // HLF.TransitionStep.Pause1Second,
            HLF.TransitionStep.HideLoadScreen,
            HLF.TransitionStep.AttachNewInFront,
            HLF.TransitionStep.AnimNewIn,
        ];
        PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
            MainMenu.NAME,
            LoadScreen.NAME,
            ...steps,
        ));
    }

    protected OnBlackoutClicked() {
        PubSub.publishSync("blackout clicked");
        super.OnBlackoutClicked();
        if (this._clickBackdropToClose === true) {
            const steps = [
                HLF.TransitionStep.ShowLoadScreen,
                // HLF.TransitionStep.AnimCurrentOut,
                HLF.TransitionStep.RemoveCurrent,
                HLF.TransitionStep.UnloadAssets,
                HLF.TransitionStep.LoadAssets,
                // HLF.TransitionStep.Pause1Second,
                HLF.TransitionStep.HideLoadScreen,
                HLF.TransitionStep.AttachNewInFront,
                HLF.TransitionStep.AnimNewIn,
            ];
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
                MainMenu.NAME,
                LoadScreen.NAME,
                ...steps,
            ));
        }
    }
}
